import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import { Box, Link, Typography } from "@mui/material";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { TestId } from "../../constants/testIds";
import type translations from "../../translations/translations.json";
import type { Order } from "../../types/order";
import { formatOrderLocations } from "../../types/order";
import OrderStatusChip from "../common/OrderStatusChip";
import { formatLicensePlateInput } from "../../utils/formatLicensePlate";
import DetailSectionCard from "../common/DetailSectionCard";
import UserAvatar from "../common/UserAvatar";
import OrderProofAttachments from "./OrderProofAttachments";

type TranslationKeys = keyof typeof translations.en;

interface OrderContextSummaryCardsProps {
  order: Order;
  initialProofAttachmentId?: string;
  onProofViewerClose?: () => void;
  hideSuratJalanLink?: boolean;
}

const FieldBlock: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600 }} component="div">
      {value}
    </Typography>
  </Box>
);

const PersonValue: React.FC<{
  user: { _id: string; name?: string; profilePictureUrl?: string | null };
}> = ({ user }) => {
  const name = user.name || user._id;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <UserAvatar user={user} size={28} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {name}
      </Typography>
    </Box>
  );
};

const formatCreationTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

const getCreationInfo = (
  order: Order,
): {
  timestamp: number;
  userId: string;
  userName?: string;
  profilePictureUrl?: string | null;
} | null => {
  const histories = order.histories ?? [];
  if (histories.length === 0) {
    return null;
  }

  const oldest = [...histories].sort((a, b) => a.timestamp - b.timestamp)[0];
  return {
    timestamp: oldest.timestamp,
    userId: oldest.userId,
    userName: oldest.userName,
    profilePictureUrl: oldest.profilePictureUrl,
  };
};

const OrderContextSummaryCards: React.FC<OrderContextSummaryCardsProps> = ({
  order,
  initialProofAttachmentId,
  onProofViewerClose,
  hideSuratJalanLink = false,
}) => {
  const { t } = useTranslation();
  const creation = getCreationInfo(order);
  const createdByUser = creation
    ? {
        _id: creation.userId,
        name: creation.userName || creation.userId,
        profilePictureUrl: creation.profilePictureUrl,
      }
    : null;
  const reportUrl = order.uangJalanReportReadableId
    ? `/uang-jalan-reports/${order.uangJalanReportReadableId}`
    : null;

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        mb: 2,
      }}
    >
      <DetailSectionCard
        title={t("orders.generalSection" as TranslationKeys)}
        icon={<AssignmentIndOutlinedIcon fontSize="small" />}
        iconColor="#7C3AED"
      >
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <FieldBlock
            label={t("orders.createdAt" as TranslationKeys)}
            value={creation ? formatCreationTimestamp(creation.timestamp) : "—"}
          />
          <FieldBlock
            label={t("orders.createdBy" as TranslationKeys)}
            value={createdByUser ? <PersonValue user={createdByUser} /> : "—"}
          />
          <FieldBlock
            label={t("orders.report" as TranslationKeys)}
            value={
              reportUrl && order.uangJalanReportReadableId ? (
                <Link
                  component={RouterLink}
                  to={reportUrl}
                  underline="hover"
                  data-testid={TestId.ORDER.VIEW_REPORT}
                  sx={{ fontWeight: 600 }}
                >
                  {order.uangJalanReportReadableId}
                </Link>
              ) : (
                "—"
              )
            }
          />
          {!hideSuratJalanLink && (
            <FieldBlock
              label={t("Surat Jalan" as TranslationKeys)}
              value={
                order.suratJalan ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Link
                      component={RouterLink}
                      to={`/surat-jalan/${order.suratJalan.readableId}`}
                      underline="hover"
                      data-testid={TestId.ORDER_SURAT_JALAN.LINK}
                      sx={{ fontWeight: 600 }}
                    >
                      {order.suratJalan.readableId}
                    </Link>
                    <OrderStatusChip status={order.suratJalan.status} />
                  </Box>
                ) : (
                  "—"
                )
              }
            />
          )}
          <FieldBlock
            label={t("Team" as TranslationKeys)}
            value={order.teamOperations?.name || "—"}
          />
          {order.note ? (
            <Box sx={{ gridColumn: { sm: "1 / -1" } }} data-testid={TestId.ORDER.NOTE}>
              <FieldBlock
                label={t("orders.note" as TranslationKeys)}
                value={
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "pre-wrap" }}>
                    {order.note}
                  </Typography>
                }
              />
            </Box>
          ) : null}
          {(order.proofAttachments?.length ?? 0) > 0 ? (
            <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
              <FieldBlock
                label={t("orders.proofImage" as TranslationKeys)}
                value={
                  <OrderProofAttachments
                    attachments={order.proofAttachments ?? []}
                    initialAttachmentId={initialProofAttachmentId}
                    onViewerClose={onProofViewerClose}
                  />
                }
              />
            </Box>
          ) : null}
        </Box>
      </DetailSectionCard>

      <DetailSectionCard
        title={t("orders.vehicleAndRouteSection" as TranslationKeys)}
        icon={<DirectionsCarFilledOutlinedIcon fontSize="small" />}
        iconColor="#16A34A"
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
            }}
          >
            <FieldBlock
              label={t("License Plate" as TranslationKeys)}
              value={
                order.truck?.licensePlate ? formatLicensePlateInput(order.truck.licensePlate) : "—"
              }
            />
            <FieldBlock
              label={t("DRIVER" as TranslationKeys)}
              value={
                order.driver ? (
                  <PersonValue
                    user={{
                      _id: order.driver._id,
                      name: order.driver.name,
                      profilePictureUrl: order.driver.profilePictureUrl,
                    }}
                  />
                ) : (
                  "—"
                )
              }
            />
            <FieldBlock
              label={t("Departure" as TranslationKeys)}
              value={order.departureTimestamp || "—"}
            />
          </Box>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            <FieldBlock
              label={t("orders.load" as TranslationKeys)}
              value={formatOrderLocations(order.load)}
            />
            <FieldBlock
              label={t("orders.unload" as TranslationKeys)}
              value={formatOrderLocations(order.unload)}
            />
            <FieldBlock
              label={t("Customer" as TranslationKeys)}
              value={order.customer?.name || "—"}
            />
            <FieldBlock
              label={t("No. LT/POD" as TranslationKeys)}
              value={order.externalOrderId || "—"}
            />
          </Box>
        </Box>
      </DetailSectionCard>
    </Box>
  );
};

export default OrderContextSummaryCards;
